import type { DiscoveryDateRange } from './filters'

export interface DateRangeQuery {
  from?: string
  to?: string
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + days)
  return copy
}

/** Monday = 1 … Sunday = 7, matching the upstream Dart weekday. */
function isoWeekday(date: Date): number {
  return date.getDay() === 0 ? 7 : date.getDay()
}

/**
 * Explicit local bounds from upstream `_dateRangeBounds` (3b39bc7):
 *
 * - today:      today 00:00 → tomorrow 00:00 − 1 ms
 * - tomorrow:   tomorrow 00:00 → day after 00:00 − 1 ms
 * - this week:  today 00:00 → next Monday 00:00 − 1 ms
 * - this month: today 00:00 → first day of next month 00:00 − 1 ms
 * - all:        no bounds
 *
 * The API filters on `startAt`, so the last millisecond keeps the whole final
 * local day inside the range without sending a next-day midnight timestamp.
 */
export function buildDateRange(
  range: DiscoveryDateRange,
  now: Date = new Date(),
): DateRangeQuery {
  if (range === 'any') return {}

  const today = startOfDay(now)
  let start: Date
  let endExclusive: Date

  switch (range) {
    case 'today':
      start = today
      endExclusive = addDays(today, 1)
      break
    case 'tomorrow':
      start = addDays(today, 1)
      endExclusive = addDays(start, 1)
      break
    case 'week':
      start = today
      endExclusive = addDays(today, 8 - isoWeekday(today))
      break
    case 'month':
      start = today
      endExclusive = new Date(today.getFullYear(), today.getMonth() + 1, 1)
      break
    default:
      return {}
  }

  return {
    from: start.toISOString(),
    to: new Date(endExclusive.getTime() - 1).toISOString(),
  }
}
